import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import { useB2bLeadsQuery, useSaveB2bLeadStatusMutation } from "@queries/website";
import "@styles/opencart-admin.css";

const KINDS = [
  { key: "service-requests", label: "Service Requests" },
  { key: "inquiries", label: "Contact Inquiries" },
  { key: "franchise-applications", label: "Franchise Applications" }
];

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];

const statusColors = {
  new: "#b45309",
  contacted: "#1d4ed8",
  quoted: "#7c3aed",
  won: "#15803d",
  lost: "#b91c1c"
};

const idOf = (kind, row) =>
  kind === "service-requests"
    ? row.id
    : kind === "inquiries"
    ? row.inquiry_id
    : row.application_id;

export default function B2bLeadsPage() {
  const [kind, setKind] = React.useState("service-requests");
  const [filters, setFilters] = React.useState({ status: "", q: "" });
  const [page, setPage] = React.useState(1);
  const [expanded, setExpanded] = React.useState(null);
  const pageSize = 20;

  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, isError, error } = useB2bLeadsQuery(
    kind,
    { status: filters.status || undefined, q: filters.q || undefined },
    page,
    pageSize
  );

  const statusMutation = useSaveB2bLeadStatusMutation();

  const rows = data?.data || [];
  const total = data?.total ?? data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  async function changeStatus(row, status) {
    try {
      await statusMutation.mutateAsync({ kind, id: idOf(kind, row), status });
      queryClient.invalidateQueries({ queryKey: ["website", "b2b", kind] });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update status");
    }
  }

  function switchKind(k) {
    setKind(k);
    setPage(1);
    setExpanded(null);
    setFilters({ status: "", q: "" });
  }

  const fmt = (d) => (d ? String(d).replace("T", " ").substring(0, 16) : "—");

  const csv = (s) =>
    (s || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  function statusSelect(row) {
    return (
      <select
        className="oc-input"
        style={{
          color: statusColors[row.status] || "#333",
          fontWeight: 600,
          maxWidth: 130
        }}
        value={row.status || "new"}
        onChange={(e) => changeStatus(row, e.target.value)}
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>B2B Leads</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Website</span>
          <span className="sep">›</span>
          <span>B2B Leads</span>
        </nav>
      </div>

      <div className="oc-card">
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {KINDS.map((k) => (
            <button
              key={k.key}
              className={`oc-btn ${kind === k.key ? "primary" : "ghost"}`}
              onClick={() => switchKind(k.key)}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 12, maxWidth: 520 }}>
          <div className="form-group" style={{ margin: 0, flex: 1 }}>
            <label>Status</label>
            <select
              className="oc-input"
              value={filters.status}
              onChange={(e) => {
                setFilters((p) => ({ ...p, status: e.target.value }));
                setPage(1);
              }}
            >
              <option value="">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, flex: 2 }}>
            <label>Search (name / email / phone)</label>
            <input
              className="oc-input"
              value={filters.q}
              onChange={(e) => {
                setFilters((p) => ({ ...p, q: e.target.value }));
                setPage(1);
              }}
            />
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {error?.message || "Unknown error"}
          </div>
        )}

        {/* -------- Service Requests -------- */}
        {kind === "service-requests" && (
          <table className="oc-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>ID</th>
                <th>Business</th>
                <th>Contact</th>
                <th style={{ width: 110 }}>Frequency</th>
                <th style={{ width: 140 }}>Submitted</th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 90 }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                    No service requests
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr>
                    <td>{row.id}</td>
                    <td>
                      {row.business_name}
                      <div style={{ fontSize: 12, color: "#777" }}>{row.business_type}</div>
                    </td>
                    <td>
                      {row.contact_name}
                      <div style={{ fontSize: 12, color: "#777" }}>
                        {row.email} · {row.telephone}
                        {row.preferred_time ? ` · prefers ${row.preferred_time}` : ""}
                      </div>
                    </td>
                    <td>
                      {row.frequency}
                      {row.frequency_days ? (
                        <div style={{ fontSize: 12, color: "#777" }}>{row.frequency_days}</div>
                      ) : null}
                    </td>
                    <td>{fmt(row.created_at)}</td>
                    <td>{statusSelect(row)}</td>
                    <td>
                      <button
                        className="oc-btn ghost"
                        onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                      >
                        {expanded === row.id ? "hide" : "view"}
                      </button>
                    </td>
                  </tr>
                  {expanded === row.id && (
                    <tr>
                      <td colSpan={7} style={{ background: "#f8fafc" }}>
                        <div style={{ display: "flex", gap: 32, padding: 8, flexWrap: "wrap" }}>
                          <div>
                            <strong>Laundry & Dry Clean</strong>
                            <ul style={{ margin: "4px 0 0 18px" }}>
                              {csv(row.laundry_services).map((s) => (
                                <li key={s}>{s}</li>
                              ))}
                              {csv(row.laundry_services).length === 0 && <li>—</li>}
                            </ul>
                          </div>
                          <div>
                            <strong>On-site</strong>
                            <ul style={{ margin: "4px 0 0 18px" }}>
                              {csv(row.onsite_services).map((s) => (
                                <li key={s}>{s}</li>
                              ))}
                              {csv(row.onsite_services).length === 0 && <li>—</li>}
                            </ul>
                          </div>
                          <div>
                            <strong>Quantities</strong>
                            <ul style={{ margin: "4px 0 0 18px" }}>
                              {Object.entries(row.quantities || {}).map(([k, v]) =>
                                v ? (
                                  <li key={k}>
                                    {k}: {typeof v === "object" ? JSON.stringify(v) : String(v)}
                                  </li>
                                ) : null
                              )}
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* -------- Contact Inquiries -------- */}
        {kind === "inquiries" && (
          <table className="oc-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>ID</th>
                <th>Business</th>
                <th>Contact</th>
                <th style={{ width: 110 }}>Vertical</th>
                <th>Message</th>
                <th style={{ width: 140 }}>Submitted</th>
                <th style={{ width: 140 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                    No inquiries
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.inquiry_id}>
                  <td>{row.inquiry_id}</td>
                  <td>
                    {row.business_name}
                    <div style={{ fontSize: 12, color: "#777" }}>{row.country}</div>
                  </td>
                  <td>
                    {row.contact_name}
                    <div style={{ fontSize: 12, color: "#777" }}>
                      {row.email} · {row.telephone}
                    </div>
                  </td>
                  <td>{row.vertical}</td>
                  <td style={{ fontSize: 13 }}>{row.message}</td>
                  <td>{fmt(row.created_at)}</td>
                  <td>{statusSelect(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* -------- Franchise Applications -------- */}
        {kind === "franchise-applications" && (
          <table className="oc-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>ID</th>
                <th>Applicant</th>
                <th style={{ width: 110 }}>City</th>
                <th style={{ width: 150 }}>Capital</th>
                <th style={{ width: 110 }}>Biz Exp.</th>
                <th>Why WashyWash</th>
                <th style={{ width: 140 }}>Submitted</th>
                <th style={{ width: 140 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 24 }}>
                    No applications
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.application_id}>
                  <td>{row.application_id}</td>
                  <td>
                    {row.full_name}
                    <div style={{ fontSize: 12, color: "#777" }}>
                      {row.email} · {row.telephone}
                    </div>
                  </td>
                  <td>{row.city}</td>
                  <td>{row.capital_range}</td>
                  <td>{row.has_business_exp ? "yes" : "no"}</td>
                  <td style={{ fontSize: 13 }}>{row.why_washywash}</td>
                  <td>{fmt(row.created_at)}</td>
                  <td>{statusSelect(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div
        className="oc-card"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> ({total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="oc-btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </button>
          <button className="oc-btn ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
